/**
 * RecalibrationProgress — web caller wrapper around the shared
 * unified component.
 *
 * Apr 28, 2026 (Recal Hardening Sprint, PR 7, G19):
 *   The full implementation now lives at
 *   web/src/components/unified/RecalibrationProgress.tsx and is shared
 *   with native (which previously had its own copy-pasted variant
 *   inside ChatScreen.native.tsx). This wrapper supplies the web-only
 *   bits the unified component asks for via injection:
 *     - useNavigate from react-router-dom for the CTA route push
 *     - storage.getItem('jwt_token') as the auth token resolver
 *     - resolveActivePlanId — web caller currently has no
 *       in-memory plan cache; we return null and let the unified
 *       component fall back to fallbackPlanRoute.
 *
 *   Existing callers (web/src/pages/Chat.tsx) keep their thin
 *   `onDismiss` signature; the unified component's richer
 *   (succeeded, scope, entityId, uiClass) signature falls through
 *   harmlessly thanks to JS argument truncation.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { storage } from '../services/storage';
import { API_BASE_URL } from '../utils/api';
import { UnifiedRecalibrationProgress } from './unified';

interface RecalibrationProgressProps {
  trackingId: string;
  recalibrationType?: string;
  onDismiss?: () => void;
  /**
   * SR-RECAL (Phase 1.6): caller posts a ChatErrorCard message into
   * the chat thread when the auto-retry budget is exhausted. Mirrors
   * the failed-chat-message UX so the user sees one consistent
   * failure surface.
   */
  onExhausted?: (trackingId: string) => void;
}

export const RecalibrationProgress: React.FC<RecalibrationProgressProps> = ({
  trackingId,
  recalibrationType,
  onDismiss,
  onExhausted,
}) => {
  const navigate = useNavigate();

  return (
    <UnifiedRecalibrationProgress
      trackingId={trackingId}
      recalibrationType={recalibrationType}
      apiBaseUrl={API_BASE_URL}
      getAuthToken={async () => storage.getItem('jwt_token')}
      onDismiss={() => {
        onDismiss?.();
      }}
      onExhausted={onExhausted}
      onCtaPress={(route) => {
        navigate(route, { state: { source: 'recalibration-complete', from: 'chat' } });
      }}
      // Web has no synchronous active-plan-id cache yet — fall back
      // to the my-path tab when scope=plan/framework needs a plan id.
      resolveActivePlanId={() => null}
      fallbackPlanRoute="/(tabs)/mypath"
    />
  );
};
